/*
 * Typed fetch client for the Beacon API.
 *
 * - Cookie session (credentials: "include"); X-CSRF-Token on every mutation.
 * - Errors become ApiError with the server's code.
 * - Step-up (spec 3.3): a 401 { code: "step_up_required" } hands control to whoever
 *   registered a step-up handler (the StepUpSheet). If it resolves, the request is
 *   retried once; if it rejects, the original error propagates.
 * - SSE for the copilot: POST + text/event-stream read through fetch, since
 *   EventSource cannot POST.
 */
import type { ApiErrorBody, ApiErrorCode, CopilotEvent } from "./types";

export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

export class ApiError extends Error {
  status: number;
  code: ApiErrorCode;
  details: ApiErrorBody["details"];
  constructor(status: number, body: Partial<ApiErrorBody>) {
    super(body.message || `Request failed (${status})`);
    this.status = status;
    this.code = (body.code as ApiErrorCode) || (status === 401 ? "unauthenticated" : "internal");
    this.details = body.details;
  }
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (Array.isArray(this.details)) for (const d of this.details) out[d.field] = d.message;
    return out;
  }
}

let csrfToken: string | null = null;
export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

type StepUpHandler = (reason: string) => Promise<void>;
let stepUpHandler: StepUpHandler | null = null;
/** The app shell registers the sheet that performs a fresh Telegram login. */
export function onStepUpRequired(handler: StepUpHandler | null) {
  stepUpHandler = handler;
}

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;
export function onUnauthorized(handler: UnauthorizedHandler | null) {
  unauthorizedHandler = handler;
}

export type Query = Record<string, string | number | boolean | null | undefined>;

export function buildUrl(path: string, query?: Query): string {
  const qs = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      qs.set(k, String(v));
    }
  }
  const s = qs.toString();
  return API_BASE + path + (s ? `?${s}` : "");
}

async function parseError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> = {};
  try {
    body = await res.json();
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(res.status, body);
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

async function send<T>(method: Method, path: string, body?: unknown, query?: Query, retried = false): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["X-CSRF-Token"] = csrfToken;
  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.ok) {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }
  const err = await parseError(res);
  if (err.code === "step_up_required" && stepUpHandler && !retried) {
    await stepUpHandler(err.message);
    return send<T>(method, path, body, query, true);
  }
  if (err.code === "unauthenticated" && unauthorizedHandler && !path.startsWith("/auth") && path !== "/me") {
    unauthorizedHandler();
  }
  throw err;
}

export const api = {
  get: <T>(path: string, query?: Query) => send<T>("GET", path, undefined, query),
  post: <T>(path: string, body?: unknown, query?: Query) => send<T>("POST", path, body ?? {}, query),
  put: <T>(path: string, body?: unknown) => send<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => send<T>("PATCH", path, body ?? {}),
  del: <T>(path: string, body?: unknown) => send<T>("DELETE", path, body),
};

/**
 * POST that answers with text/event-stream. Each event is `event: <type>` + `data: <json>`.
 * Resolves when the stream closes; rejects on HTTP errors (after one step-up retry).
 */
export async function postStream(
  path: string,
  body: unknown,
  onEvent: (e: CopilotEvent) => void,
  signal?: AbortSignal,
  retried = false,
): Promise<void> {
  const headers: Record<string, string> = { "Content-Type": "application/json", Accept: "text/event-stream" };
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  const res = await fetch(buildUrl(path), { method: "POST", headers, credentials: "include", body: JSON.stringify(body), signal });
  if (!res.ok) {
    const err = await parseError(res);
    if (err.code === "step_up_required" && stepUpHandler && !retried) {
      await stepUpHandler(err.message);
      return postStream(path, body, onEvent, signal, true);
    }
    throw err;
  }
  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const chunk = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const data = chunk
        .split("\n")
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trimStart())
        .join("\n");
      if (!data) continue;
      try {
        onEvent(JSON.parse(data) as CopilotEvent);
      } catch {
        /* ignore malformed frames */
      }
    }
  }
}
